import React, { useState, useEffect } from "react";
import { PayAnnouncementBar } from "@/components/pay/PayOptions";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag, Phone, User, Crown, ArrowRight } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { NAV_EN, HEADER_EN } from "@/en/nav";
import { NAV_NL, HEADER_NL } from "@/nl/nav";
import { langOfPath, equivalentPath } from "@/locale/alternates";

const NAV = [
  { to: "/fotoboxen", label: "Fotoboxen" },
  { to: "/vergleich", label: "Modelle vergleichen" },
  { to: "/kreulbooth", label: "Software" },
  { to: "/zubehoer", label: "Zubehör" },
  { to: "/service", label: "Service & Support" },
  { to: "/ueber-uns", label: "Über uns" },
];
const NAV_EN_FULL = NAV_EN.filter((n) => n.to !== "/");
// Fremdsprachige Bereiche: Navigation + Kopf-Texte (klein, eigene Dateien en/nav.js, nl/nav.js)
const INTL = { en: { nav: NAV_EN_FULL, h: HEADER_EN }, nl: { nav: NAV_NL, h: HEADER_NL } };

/** Kleine Flaggen als SVG (Emoji-Flaggen sehen je nach Gerät unterschiedlich aus oder fehlen). */
function FlagDE({ className = "" }) {
  return (
    <svg viewBox="0 0 5 3" className={className} aria-hidden="true"><rect width="5" height="1" fill="#000" /><rect y="1" width="5" height="1" fill="#D00" /><rect y="2" width="5" height="1" fill="#FFCE00" /></svg>
  );
}
function FlagGB({ className = "" }) {
  return (
    <svg viewBox="0 0 60 30" className={className} aria-hidden="true">
      <clipPath id="fb-gb"><rect width="60" height="30" /></clipPath>
      <g clipPath="url(#fb-gb)">
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="2" />
        <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
      </g>
    </svg>
  );
}

function FlagNL({ className = "" }) {
  return (
    <svg viewBox="0 0 9 6" className={className} aria-hidden="true"><rect width="9" height="2" fill="#AE1C28" /><rect y="2" width="9" height="2" fill="#FFF" /><rect y="4" width="9" height="2" fill="#21468B" /></svg>
  );
}

const LANGS = [
  { lang: "de", to: "/", title: "Deutsch", Flag: FlagDE },
  { lang: "en", to: "/en", title: "English", Flag: FlagGB },
  { lang: "nl", to: "/nl", title: "Nederlands", Flag: FlagNL },
];

/** Sprachumschalter: immer sichtbar (auch auf dem Handy), Deutsch / English / Nederlands als Flaggen. */
function LanguageSwitch({ lang, pathname }) {
  const base = "inline-flex items-center justify-center w-8 h-7 rounded-md transition-all";
  return (
    <div className="inline-flex items-center gap-0.5 p-0.5 rounded-lg border border-hair bg-white" data-testid="language-switch" role="group" aria-label={INTL[lang]?.h.languages || "Sprache"}>
      {LANGS.map(({ lang: l, to, title, Flag }) => (
        <Link key={l} to={equivalentPath(pathname, l) || to} title={title} aria-label={title} data-testid={`lang-${l}`} className={`${base} ${lang === l ? "bg-cream ring-1 ring-brand" : "opacity-60 hover:opacity-100"}`}>
          <Flag className="w-5 h-3 rounded-[2px]" />
        </Link>
      ))}
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { count } = useCart();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const lang = langOfPath(pathname);
  const intl = INTL[lang] || null; // null = deutscher Shop
  const en = !!intl; // fremdsprachiger Bereich: ohne Konto/Warenkorb
  const nav = intl ? intl.nav : NAV;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-300 ${
        scrolled ? "shadow-[0_1px_0_rgba(26,26,29,0.08)]" : ""
      }`}
      data-testid="site-header"
    >
      {!intl && <PayAnnouncementBar />}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2">
          <span className="inline-flex flex-col leading-none">
            <span className="font-heading text-lg md:text-xl font-semibold tracking-tight text-ink">
              fotoboxverkauf<span className="text-brand">.de</span>
            </span>
            <span className="mt-0.5 text-[10px] md:text-[11px] tracking-wide text-ink-muted font-medium">
              Kreul Media Group
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              end={n.to === "/" || n.to === "/en" || n.to === "/nl"}
              className={({ isActive }) =>
                `text-sm transition-colors ${isActive ? "text-brand font-medium" : "text-ink-soft hover:text-ink"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-4">
          <LanguageSwitch lang={lang} pathname={pathname} />
          {en ? (
            <>
              <Link to={intl.h.contactPath} className="hidden md:inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand transition-colors" data-testid={`header-${lang}-contact`}><Phone className="w-4 h-4" /> {intl.h.contact}</Link>
              <Link to={intl.h.contactPath} className="hidden lg:inline-flex text-sm px-3 py-1.5 rounded-full bg-ink text-white hover:bg-brand hover:text-ink transition-colors" data-testid={`header-${lang}-quote`}>{intl.h.quote}</Link>
            </>
          ) : (<>
          <Link
            to="/kontakt"
            data-testid="header-consult"
            className="hidden md:inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand transition-colors"
          >
            <Phone className="w-4 h-4" /> Beratung
          </Link>
          {user ? (
            <Link
              to={["admin","staff"].includes(user.role) ? "/admin" : "/konto"}
              data-testid="header-account"
              className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cream text-sm text-ink hover:bg-brand-soft hover:text-brand transition-colors font-medium"
            >
              <User className="w-4 h-4" /> {user.first_name || "Konto"}
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="header-login"
                className="hidden md:inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand transition-colors"
              >
                <User className="w-4 h-4" /> Anmelden
              </Link>
              <Link
                to="/registrieren"
                data-testid="header-register"
                className="hidden lg:inline-flex text-sm px-3 py-1.5 rounded-full bg-ink text-white hover:bg-brand hover:text-ink transition-colors"
              >
                Kundenkonto
              </Link>
            </>
          )}
          </>)}
          {(!en || count > 0) && (
          <Link
            to={{ en: "/en/cart", nl: "/nl/winkelwagen" }[lang] || "/warenkorb"}
            data-testid="header-cart"
            aria-label={{ en: "Cart", nl: "Winkelwagen" }[lang] || "Warenkorb"}
            className="relative p-2 text-ink hover:text-brand transition-colors"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -right-0.5 bg-brand text-[10px] font-medium w-5 h-5 rounded-full flex items-center justify-center text-ink"
              >
                {count}
              </span>
            )}
          </Link>
          )}
          <button
            className="lg:hidden p-2 text-ink"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-nav-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-hair bg-white">
          <div className="px-6 py-6 flex flex-col gap-4">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-base text-ink hover:text-brand"
              >
                {n.label}
              </Link>
            ))}
            <Link to={intl ? intl.h.contactPath : "/kontakt"} onClick={() => setOpen(false)} className="text-base text-ink hover:text-brand">
              {intl ? intl.h.quote : "Beratung buchen"}
            </Link>
            {!en && (
            <div className="pt-4 border-t border-hair flex gap-3">
              {user ? (
                <Link to={["admin","staff"].includes(user.role) ? "/admin" : "/konto"} onClick={() => setOpen(false)} className="btn-primary flex-1 justify-center text-sm py-2" data-testid="mobile-nav-account">
                  <User className="w-4 h-4" /> {["admin","staff"].includes(user.role) ? "Admin" : "Mein Konto"}
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-secondary flex-1 justify-center text-sm py-2" data-testid="mobile-nav-login">Anmelden</Link>
                  <Link to="/registrieren" onClick={() => setOpen(false)} className="btn-primary flex-1 justify-center text-sm py-2" data-testid="mobile-nav-register">Kundenkonto</Link>
                </>
              )}
            </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
